import React, { useEffect, useState, useRef } from "react";
import { fireStore } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";

import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const FSOMonthly = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 1);

  const [startDate, setStartDate] = useState(threeDaysAgo.getTime());
  const [endDate, setEndDate] = useState(Date.now());

  const [FSOReports, setFSOReports] = useState([]);
  const [filteredFSOReports, setFilteredFSOReports] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");

  // Get Reports...
  useEffect(() => {
    const fetchFSOReports = async () => {
      try {
        const collectionRef = collection(fireStore, "FSOReports");
        const q = query(
          collectionRef,
          where("id", ">=", startDate),
          where("id", "<=", endDate)
        );

        const querySnapshot = await getDocs(q);

        const sortedReportsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFSOReports(sortedReportsList);
        setFilteredFSOReports(sortedReportsList);
        // console.log(FSOReportsList);
      } catch (error) {
        console.error("Error fetching FSOReports:", error);
      }
    };

    fetchFSOReports();
  }, [startDate, endDate]);

  // Get Territories....
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };

    fetchTerritories();
  }, []);

  // Filter Logbooks...
  useEffect(() => {
    const filtered = FSOReports.filter(
      (book) =>
        book.user.territory.name.toLowerCase() ===
        selectedTerritory.toLowerCase()
    );
    setFilteredFSOReports(filtered);
    if (selectedTerritory === "All") {
      setFilteredFSOReports(FSOReports);
    }
  }, [selectedTerritory]);

  // Filter Logbooks...
  useEffect(() => {
    const filtered = FSOReports.filter((book) =>
      book.user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredFSOReports(filtered);
    if (searchQuery === "") {
      setFilteredFSOReports(FSOReports);
    }
  }, [searchQuery]);

  // Export to PDF...
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20; // Add margin for better formatting
    let yPosition = margin;

    // Temporarily show the element if it's hidden
    const FSOMonthlyContainer = document.getElementById("FSOMonthlyContainer");
    const originalDisplay = FSOMonthlyContainer.style.display;
    FSOMonthlyContainer.style.display = "block";
    await new Promise((resolve) => setTimeout(resolve, 100)); // Allow the layout to update

    // Capture the entire table including the header
    const tableElement = document.querySelector("#FSOMonthlyContainer table");
    if (tableElement) {
      const canvas = await html2canvas(tableElement, { scale: 1 });
      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      const imgWidth = pageWidth - 2 * margin; // Account for margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight > pageHeight - 2 * margin) {
        // Calculate the number of pages needed
        const numPages = Math.ceil(imgHeight / (pageHeight - 2 * margin));
        let startY = 0;

        for (let i = 0; i < numPages; i++) {
          if (i > 0) {
            doc.addPage();
            yPosition = margin; // Reset yPosition for new page
          }

          const sliceHeight = pageHeight - 2 * margin;
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = imgWidth * 2;
          sliceCanvas.height = sliceHeight * 2;
          const sliceCtx = sliceCanvas.getContext("2d");
          const sliceImg = new Image();
          sliceImg.src = imgData;

          sliceImg.onload = () => {
            sliceCtx.drawImage(sliceImg, 0, -startY, imgWidth * 2, imgHeight);
            const sliceImgData = sliceCanvas.toDataURL("image/png");
            doc.addImage(
              sliceImgData,
              "PNG",
              margin,
              yPosition,
              imgWidth,
              sliceHeight
            );
            yPosition += sliceHeight;
            startY += sliceHeight;
          };
        }
      } else {
        doc.addImage(imgData, "PNG", margin, yPosition, imgWidth, imgHeight);
      }
    }

    doc.save("FSOReports.pdf");

    // Restore the original display style
    FSOMonthlyContainer.style.display = originalDisplay;
  };

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      <div className="bg-white px-4 py-2 rounded-lg shadow-lg max-w-5xl mx-auto mt-2 lg:sticky top-0 z-50">
        <div className="flex flex-wrap justify-between items-center py-2">
          <h1 className="text-lg font-semibold text-liquidblue">
            FSO Monthly ({filteredFSOReports.length})
          </h1>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-3 py-1"
          >
            PDF
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={selectedTerritory}
            onChange={(e) => setSelectedTerritory(e.target.value)}
          >
            <option value="All">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          <div className="flex items-center">
            {/* From Date Picker */}
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              value={
                startDate ? new Date(startDate).toISOString().split("T")[0] : ""
              }
              onChange={(e) => {
                if (e?.target?.value) {
                  setStartDate(new Date(e.target.value).getTime());
                }
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center">
            <label
              htmlFor="to-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              To Date
            </label>
            <input
              type="date"
              value={
                startDate ? new Date(startDate).toISOString().split("T")[0] : ""
              }
              onChange={(e) => {
                if (e?.target?.value) {
                  setStartDate(new Date(e.target.value).getTime());
                }
              }}
            />
          </div>
        </div>
      </div>
      {filteredFSOReports.map((item) => (
        <div
          id={`FSOReport-${item.id}`}
          key={item.id}
          className="bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto mt-4"
        >
          {/*<h1 className="text-xl font-semibold py-4 text-liquidblue">{item.user?.territory?.name}</h1>*/}

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="flex justify-between">
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.name}
                    <br />
                    <p className="text-gray-400">{item.user?.phone}</p>
                  </th>
                  <th className="text-darkteal py-2 px-4 border-b border-gray-200 text-left">
                    {item.month}
                  </th>

                  <th className="py-2 px-4 border-b text-gray-500 text-left">
                    {new Date(item.id).toLocaleDateString()}
                    <p className="text-gray-400">
                      {new Date(item.id).toLocaleTimeString()}
                    </p>
                  </th>

                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.territory?.name ? (
                      <h1 className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-4 py-1">
                        {item.user.territory.name}
                      </h1>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="flex flex-row w-full">
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-7">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Month</span>
                        <span className=" font-lato font-bold">
                          {item.month}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">FSO Name</span>
                        <span className=" font-lato font-bold">
                          {item.fsoName}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Dealer Name</span>
                        <span className=" font-lato font-bold">
                          {item.dealerName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Salary</span>
                        <span className=" font-lato font-bold">
                          {item.salary || "N/A"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-7">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Sales</span>
                        <span className=" font-lato font-bold">
                          {item.sales}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Collection</span>
                        <span className=" font-lato font-bold">
                          {item.collection}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">FMFD </span>
                        <span className=" font-lato font-bold">
                          {item.fmfdCount}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <span className="font-lato font-bold">Remarks</span>
                      </div>
                      <div className="flex items-center">
                        <p className="w-full break-words font-lato font-extrabold">
                          {item.remarks}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div id="FSOMonthlyContainer" class="bg-gray-100 hidden">
        <div class="container mx-auto py-8">
          <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-darkteal">
              <thead>
                <tr>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Territory
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    FSO Name
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Diamond Dealer Name
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Monthly Collection
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Monthly Sales
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    FMFD
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Salary
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Remarks
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFSOReports.map((item) => (
                  <tr key={item.id}>
                    <td class="px-4 py-2 border border-darkteal text-center">
                      {item.user?.territory?.name}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.fsoName}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.dealerName}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.collection}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.sales}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.fmfdCount}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.salary || "N/A"}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.remarks}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FSOMonthly;
